
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStores, getProducts, isStoreOpen } from '../../services/supabaseService';
import { useAuth } from '../../components/AuthProvider';
import { OfflineWarning } from '../../components/OfflineWarning';

export const Stores: React.FC = () => {
  const { isOffline } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('all');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [onlySalesEnabled, setOnlySalesEnabled] = useState(false);
  const [onlyPartners, setOnlyPartners] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openStatuses, setOpenStatuses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const unsubStores = getStores((data) => {
      setStores(data);
      setLoading(false);
    });
    const unsubProducts = getProducts((data) => {
      setProducts(data);
    });

    return () => {
      unsubStores();
      unsubProducts();
    };
  }, []);

  useEffect(() => {
    if (stores.length > 0) {
      stores.forEach(async (store) => {
        try {
          const open = await isStoreOpen(store.id);
          setOpenStatuses(prev => ({ ...prev, [store.id]: open }));
        } catch (e) {
          console.error('Error checking store open status in list:', e);
        }
      });
    }
  }, [stores]);

  // Identifica quais IDs de loja possuem produtos no marketplace
  const storesWithProducts = useMemo(() => {
    return new Set(products.map(p => p.storeId || p.slug)); // Using slug as fallback if storeId is missing in seed
  }, [products]);

  // Lista de estados únicos presentes nas lojas
  const availableStates = useMemo(() => {
    const states = stores
      .map(s => s.estado || s.state?.code)
      .filter(Boolean);
    return Array.from(new Set(states)).sort() as string[];
  }, [stores]);

  const filteredStores = useMemo(() => {
    return stores
      .filter(store => {
        // Filtro de Busca (Nome ou Cidade)
        const city = store.city || store.cidade || '';
        const searchMatch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           (city).toLowerCase().includes(searchQuery.toLowerCase());
        
        // Filtro de Estado
        const stateCode = store.estado || store.state?.code;
        const stateMatch = selectedState === 'all' || stateCode === selectedState;
        
        // Filtro de "Compra disponível" (Apenas lojas com produtos no estoque)
        const availabilityMatch = onlyAvailable ? storesWithProducts.has(store.id || store.slug) : true;

        // Filtro de "Venda habilitada"
        const salesMatch = onlySalesEnabled ? !!store.sales_enabled : true;

        // Filtro de "Apenas Parceiras"
        const partnerMatch = onlyPartners ? !!(store.isPartner || store.is_partner) : true;

        return searchMatch && stateMatch && availabilityMatch && salesMatch && partnerMatch;
      })
      .sort((a, b) => {
        const isPartnerA = a.isPartner || a.is_partner ? 1 : 0;
        const isPartnerB = b.isPartner || b.is_partner ? 1 : 0;
        if (isPartnerA !== isPartnerB) return isPartnerB - isPartnerA;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [searchQuery, selectedState, onlyAvailable, onlySalesEnabled, onlyPartners, storesWithProducts, stores]);

  const totalPartners = useMemo(() => {
    return stores.filter(s => s.isPartner || s.is_partner).length;
  }, [stores]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1700px] mx-auto pb-20 px-3 sm:px-4">
      {isOffline && <OfflineWarning />}
      
      {/* Hero Header Compacto & Leve */}
      <div className="relative mb-5 sm:mb-6 rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-xl sm:text-3xl font-black tracking-tight text-white uppercase italic">Lojas</h1>
            <p className="text-slate-400 text-xs sm:text-sm max-w-lg">
              Conecte-se com as melhores lojas de TCG do Brasil e encontre tudo para o seu deck.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {totalPartners > 0 && (
              <div className="bg-purple-950/60 border border-purple-500/30 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-xl flex items-center space-x-1.5">
                <i className="fas fa-certificate text-amber-400 text-xs"></i>
                <span className="text-[10px] font-black text-purple-200 uppercase tracking-wider">{totalPartners} {totalPartners === 1 ? 'Parceira' : 'Parceiras'}</span>
              </div>
            )}
            <div className="bg-slate-950/60 border border-slate-800 px-3 py-1 sm:py-1.5 rounded-xl">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{filteredStores.length} {filteredStores.length === 1 ? 'Loja' : 'Lojas'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar - Compacto no Mobile e Desktop */}
      <div className="mb-5 sm:mb-6 space-y-2.5">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center">
          {/* Barra de Busca Principal */}
          <div className="sm:col-span-6 relative">
            <i className="fas fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <input 
              type="text" 
              placeholder="Buscar loja por nome ou cidade..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-9 py-2.5 text-xs text-white placeholder:text-slate-500 font-medium focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
              >
                <i className="fas fa-times-circle"></i>
              </button>
            )}
          </div>

          {/* Filtro de Estado */}
          <div className="sm:col-span-3 relative">
            <i className="fas fa-location-dot absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-8 py-2.5 text-xs font-bold uppercase tracking-wider outline-none focus:border-purple-500 appearance-none cursor-pointer text-slate-300 w-full transition-colors"
            >
              <option value="all">Todos Estados</option>
              {availableStates.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
            <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
          </div>

          {/* Quick Filter Buttons Container */}
          <div className="sm:col-span-3 flex items-center gap-2">
            {/* Checkbox "Apenas Parceiras" */}
            <button 
              type="button"
              onClick={() => setOnlyPartners(!onlyPartners)}
              className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2.5 rounded-xl border transition-colors cursor-pointer text-center ${
                onlyPartners 
                ? 'bg-purple-950/80 border-purple-500 text-purple-200' 
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              <i className={`fas fa-star text-[10px] ${onlyPartners ? 'text-amber-400' : 'text-slate-500'}`}></i>
              <span className="text-[10px] font-black uppercase tracking-wider truncate">Parceiras</span>
            </button>

            {/* Checkbox "Comprável" */}
            <button 
              type="button"
              onClick={() => setOnlySalesEnabled(!onlySalesEnabled)}
              className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2.5 rounded-xl border transition-colors cursor-pointer text-center ${
                onlySalesEnabled 
                ? 'bg-emerald-950/80 border-emerald-500 text-emerald-300' 
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              <i className={`fas fa-dollar-sign text-[10px] ${onlySalesEnabled ? 'text-emerald-400' : 'text-slate-500'}`}></i>
              <span className="text-[10px] font-black uppercase tracking-wider truncate">Vendas</span>
            </button>
          </div>
        </div>
      </div>

      {/* Alert Banner / Missing Store Notice */}
      <div className="mb-5 sm:mb-6 p-3 sm:p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-3 text-left">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0">
            <i className="fas fa-shop-slash text-xs"></i>
          </div>
          <div className="space-y-0.5">
            <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-200">Não encontrou alguma loja ou parceiro?</h4>
            <p className="text-[10px] sm:text-[11px] text-slate-400 leading-normal">
              Consulte nosso FAQ com o passo a passo para solicitar o cadastro no Cardumy!
            </p>
          </div>
        </div>
        <Link 
          to="/suporte?tab=geral" 
          className="bg-purple-600/10 hover:bg-purple-600 text-purple-300 hover:text-white border border-purple-500/30 hover:border-purple-500 px-3.5 py-1.5 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-colors shrink-0 whitespace-nowrap self-stretch sm:self-auto text-center"
        >
          Ver no FAQ
        </Link>
      </div>

      {/* Grid de Lojas - Super Leve, Compacto no Mobile e Snappy */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
        {filteredStores.map((store) => {
          const isPartner = !!(store.isPartner || store.is_partner);
          const isOpen = openStatuses[store.id];
          const hasProducts = storesWithProducts.has(store.id);

          return (
            <div 
              key={store.id} 
              className={`rounded-2xl border transition-colors p-3.5 sm:p-4 flex flex-col justify-between gap-3 ${
                isPartner 
                ? 'bg-slate-900/90 border-purple-500/40 hover:border-purple-400' 
                : 'bg-slate-900/70 border-slate-800 hover:border-slate-700'
              }`}
            >
              {/* Seção Principal: Logo em Destaque na Esquerda + Informações na Direita */}
              <div className="flex items-start gap-3.5">
                {/* Logo em Destaque na Esquerda */}
                <Link 
                  to={`/loja/${store.slug || store.id}`}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl border border-slate-800 bg-slate-950 overflow-hidden flex items-center justify-center shrink-0 p-1.5 hover:border-purple-500/50 transition-colors shadow-inner"
                  title={`Ver perfil de ${store.name}`}
                >
                  <img 
                    src={store.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name || "")}`} 
                    className="w-full h-full object-contain" 
                    alt={store.name} 
                    loading="lazy"
                    decoding="async"
                  />
                </Link>

                {/* Informações da Loja na Direita */}
                <div className="flex-1 min-w-0 space-y-1.5">
                  {/* Badges de Status */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {isPartner && (
                      <span className="bg-purple-900/90 border border-purple-400/40 text-purple-200 text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider flex items-center space-x-1">
                        <i className="fas fa-star text-[7px] text-amber-300"></i>
                        <span>Parceira</span>
                      </span>
                    )}
                    {isOpen !== undefined && (
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider border ${
                        isOpen
                          ? 'bg-emerald-950/90 border-emerald-500/50 text-emerald-400'
                          : 'bg-rose-950/90 border-rose-800 text-rose-400'
                      }`}>
                        {isOpen ? 'Aberta' : 'Fechada'}
                      </span>
                    )}
                    {store.sales_enabled && (
                      <span 
                        className="text-[8px] font-black text-emerald-400 bg-emerald-950/50 border border-emerald-500/30 px-1.5 py-0.5 rounded-md uppercase tracking-wider inline-flex items-center gap-1"
                        title="Vendas habilitadas pela plataforma"
                      >
                        <i className="fas fa-cart-shopping text-[7px]"></i> ON
                      </span>
                    )}
                    {hasProducts && (
                      <span className="bg-slate-900/90 border border-slate-700 text-emerald-400 px-1.5 py-0.5 rounded-md text-[8px] font-bold" title="Produtos cadastrados">
                        <i className="fas fa-bag-shopping text-[7px]"></i>
                      </span>
                    )}
                  </div>

                  {/* Nome da Loja */}
                  <Link 
                    to={`/loja/${store.slug || store.id}`}
                    className="block group"
                  >
                    <div className="flex items-center space-x-1.5">
                      <h3 className="text-sm sm:text-base font-black text-white truncate group-hover:text-purple-300 transition-colors">
                        {store.name}
                      </h3>
                      {isPartner && (
                        <i className="fas fa-certificate text-amber-400 text-[10px] shrink-0" title="Parceira Oficial"></i>
                      )}
                    </div>
                  </Link>

                  {/* Localização */}
                  <div className="flex items-center text-slate-400 text-[10px] sm:text-[11px] space-x-1 font-medium truncate">
                    <i className="fas fa-location-dot text-pink-500/80 text-[9px] shrink-0"></i>
                    <span className="truncate uppercase tracking-tight">
                      {(store.city || store.cidade || 'Localização')} - {store.estado || store.state?.code || ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filteredStores.length === 0 && (
          <div className="col-span-full py-16 text-center space-y-3 bg-slate-900/40 rounded-2xl border border-dashed border-slate-800">
             <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-2 text-slate-600">
                <i className="fas fa-store-slash text-xl"></i>
             </div>
             <p className="text-slate-400 font-bold uppercase tracking-wider text-xs">Nenhuma loja atende aos critérios.</p>
             <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
               <button 
                type="button"
                onClick={() => { setSearchQuery(''); setSelectedState('all'); setOnlyAvailable(false); setOnlySalesEnabled(false); setOnlyPartners(false); }}
                className="text-purple-400 font-bold text-xs uppercase hover:text-white transition-colors cursor-pointer"
               >
                 Redefinir Filtros
               </button>
               <span className="text-slate-700 hidden sm:inline">•</span>
               <Link 
                to="/suporte?tab=geral"
                className="text-slate-400 font-bold text-xs uppercase hover:text-purple-400 transition-colors"
               >
                 Indicar uma loja no FAQ
               </Link>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};
